import useHashtags from "src/features/HomepageCarouselBanners/hooks/useHashtags";
import { Search, Hash, X } from "lucide-react";
import { Button } from "src/components/ui/button";
import dayjs from "dayjs";
import { useState, useRef, useEffect, useId } from "react";
import AuthorFilter from "src/features/SocialFeedContent/components/AuthorFilter/AuthorFilter";

export interface SocialPostContentFilterProps {
	search: string;
	hashtag: string;
	debouncedHashtag: string;
	author: string;
	startDate: Date;
	endDate: Date;
	isResetFiltersBtnDisabled?: boolean;
	onSearchChange: (value: string) => void;
	onHashtagChange: (hashtag: string) => void;
	onAuthorChange: (authorId: string) => void;
	onStartDateChange: (date: Date) => void;
	onEndDateChange: (date: Date) => void;
	onResetFiltersClick: () => void;
}

const SocialPostContentFilter = ({
	search,
	hashtag,
	debouncedHashtag,
	author,
	startDate,
	endDate,
	isResetFiltersBtnDisabled,
	onSearchChange,
	onHashtagChange,
	onAuthorChange,
	onStartDateChange,
	onEndDateChange,
	onResetFiltersClick
}: SocialPostContentFilterProps) => {
	const [isHashtagOpen, setIsHashtagOpen] = useState(false);
	const hashtagRef = useRef<HTMLDivElement>(null);
	const hashtagListboxId = useId();

	const { data } = useHashtags(debouncedHashtag, {
		enabled: Boolean(debouncedHashtag)
	});

	useEffect(() => {
		const handleOutsideClick = (e: MouseEvent) => {
			if (
				hashtagRef.current &&
				!hashtagRef.current.contains(e.target as Node)
			) {
				setIsHashtagOpen(false);
			}
		};
		document.addEventListener("mousedown", handleOutsideClick);
		return () => document.removeEventListener("mousedown", handleOutsideClick);
	}, []);

	const hashtags = data ? data?.pages.flatMap(({ results }) => results) : [];

	const maxStartDate = dayjs(endDate).format("YYYY-MM-DD");
	const maxEndDate = dayjs().format("YYYY-MM-DD");
	const minEndDate = dayjs(startDate).format("YYYY-MM-DD");

	return (
		<div className="bg-white border border-border rounded-xl p-4 shadow-xs">
			<div className="grid grid-cols-1 md:grid-cols-2 gap-3">
				{/* Search post body */}
				<div>
					<label htmlFor="filter-search" className="block text-sm font-medium text-zinc-600 mb-1.5">
						Search
					</label>
					<div className="relative">
						<Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
						<input
							id="filter-search"
							type="text"
							value={search}
							onChange={(e) => onSearchChange(e.target.value)}
							placeholder="Search posts..."
							className="w-full bg-white border border-border rounded-lg pl-9 pr-9 py-2 text-base shadow-xs focus:outline-none focus:ring-2 focus:ring-ring transition-colors"
						/>
						{search && (
							<button
								type="button"
								aria-label="Clear search"
								className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-muted transition-colors"
								onClick={() => onSearchChange("")}
							>
								<X className="h-4 w-4 text-zinc-400" />
							</button>
						)}
					</div>
				</div>

				{/* Author filter */}
				<AuthorFilter value={author} onChange={onAuthorChange} />

				{/* Hashtag filter */}
				<div className="relative" ref={hashtagRef}>
					<label htmlFor="filter-hashtag" className="block text-sm font-medium text-zinc-600 mb-1.5">
						Hashtag
					</label>
					<div className="relative">
						<Hash className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
						<input
							id="filter-hashtag"
							type="text"
							value={hashtag}
							onChange={(e) => {
								onHashtagChange(e.target.value);
								setIsHashtagOpen(true);
							}}
							onFocus={() => hashtag && setIsHashtagOpen(true)}
							placeholder="Filter by hashtag..."
							role="combobox"
							aria-expanded={isHashtagOpen && hashtags.length > 0}
							aria-autocomplete="list"
							aria-controls={hashtagListboxId}
							className="w-full bg-white border border-border rounded-lg pl-9 pr-9 py-2 text-base shadow-xs focus:outline-none focus:ring-2 focus:ring-ring transition-colors"
						/>
						{hashtag && (
							<button
								type="button"
								aria-label="Clear hashtag"
								className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-muted transition-colors"
								onClick={() => {
									onHashtagChange("");
									setIsHashtagOpen(false);
								}}
							>
								<X className="h-4 w-4 text-zinc-400" />
							</button>
						)}

						{isHashtagOpen && hashtags.length > 0 && (
							<div
								id={hashtagListboxId}
								role="listbox"
								className="absolute top-full left-0 z-10 bg-white border border-border rounded-lg shadow-lg max-h-60 overflow-y-auto w-full mt-1"
							>
								{hashtags.map((h, index) => (
									<button
										key={`option-${index}`}
										type="button"
										role="option"
										aria-selected={h.name === hashtag}
										className="w-full text-left px-3 py-2 text-sm hover:bg-primary-lighter transition-colors first:rounded-t-lg last:rounded-b-lg"
										onClick={() => {
											onHashtagChange(h.name);
											setIsHashtagOpen(false);
										}}
									>
										{h.name}
									</button>
								))}
							</div>
						)}
					</div>
				</div>

				{/* Date range + Reset */}
				<div className="grid grid-cols-[1fr_1fr_auto] gap-3 items-end">
					<div>
						<label htmlFor="filter-from" className="block text-sm font-medium text-zinc-600 mb-1.5">
							From
						</label>
						<input
							id="filter-from"
							type="date"
							value={startDate ? dayjs(startDate).format("YYYY-MM-DD") : ""}
							max={maxStartDate}
							onChange={(e) => onStartDateChange(new Date(e.target.value))}
							className="w-full bg-white border border-border rounded-lg px-3 py-2 text-base shadow-xs focus:outline-none focus:ring-2 focus:ring-ring transition-colors"
						/>
					</div>
					<div>
						<label htmlFor="filter-to" className="block text-sm font-medium text-zinc-600 mb-1.5">
							To
						</label>
						<input
							id="filter-to"
							type="date"
							value={endDate ? dayjs(endDate).format("YYYY-MM-DD") : ""}
							min={minEndDate}
							max={maxEndDate}
							onChange={(e) => onEndDateChange(new Date(e.target.value))}
							className="w-full bg-white border border-border rounded-lg px-3 py-2 text-base shadow-xs focus:outline-none focus:ring-2 focus:ring-ring transition-colors"
						/>
					</div>
					<Button
						type="button"
						variant="outline"
						disabled={isResetFiltersBtnDisabled}
						onClick={onResetFiltersClick}
					>
						Reset
					</Button>
				</div>
			</div>
		</div>
	);
};

export default SocialPostContentFilter;
